import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ToggleNodeView } from './toggle-node-view'
import { nanoid } from 'nanoid'

export interface ToggleAttributes {
  id?: string
  title?: string
  defaultOpen?: boolean
}

export const ToggleSpec = {
  group: 'block',
  content: 'block*',
  attrs: {
    id: { default: nanoid(10) },
    title: { default: '' },
    defaultOpen: { default: false },
  },
  toDOM() { return ['div', { 'data-toggle': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-toggle]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    toggle: {
      /**
       * Insert a toggle
       */
      insertToggle: (attributes?: ToggleAttributes) => ReturnType
      /**
       * Update toggle attributes
       */
      updateToggle: (attributes: Partial<ToggleAttributes>) => ReturnType
    }
  }
}

export const ToggleExtension = Node.create({
  name: 'toggle',

  group: 'block',

  content: 'block+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      title: {
        default: 'Toggle',
        parseHTML: (element) => element.getAttribute('data-title'),
        renderHTML: (attributes) => {
          if (!attributes.title) {
            return {}
          }
          return {
            'data-title': attributes.title,
          }
        },
      },
      defaultOpen: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-default-open') === 'true',
        renderHTML: (attributes) => {
          if (attributes.defaultOpen === undefined) {
            return {}
          }
          return {
            'data-default-open': attributes.defaultOpen,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="toggle"]',
      },
      {
        tag: 'div.toggle-item',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'toggle',
        class: 'toggle-item',
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ToggleNodeView)
  },

  addCommands() {
    return {
      insertToggle:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: 'Toggle',
              defaultOpen: false,
              ...attributes,
            },
            content: [
              {
                type: 'paragraph',
                content: []
              }
            ],
          })
        },
      updateToggle:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the toggle with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of a toggle
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})